import {z} from 'zod'

import {mapStockSource, type StockSource, stockSourceRawFields} from 'src/equipment/stock-source'

export const stockHeldElsewhereRawSchema = z.object({
    job_id: z.string(),
    job_name: z.string(),
    quantity: z.number().int(),
})

export const stockEquipmentAssignmentSummaryRawSchema = z.object({
    ...stockSourceRawFields,
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    assigned_quantity: z.number(),
    assignable_quantity: z.number().nullish(),
    applied_delta: z.number().nullish(),
    held_elsewhere: z.array(stockHeldElsewhereRawSchema).optional().default([]),
})

export type StockEquipmentAssignmentSummaryRaw = z.infer<
    typeof stockEquipmentAssignmentSummaryRawSchema
>

export type StockHeldElsewhere = {
    jobId: string
    jobName: string
    quantity: number
}

export type StockEquipmentAssignmentSummary = StockSource & {
    rentalAssetTypeId: string
    rentalAssetTypeName: string
    assignedQuantity: number
    assignableQuantity: number | null
    appliedDelta?: number
    heldElsewhere: StockHeldElsewhere[]
}

export const applyStockEquipmentAssignmentDeltaInputSchema = z.object({
    thirdPartyRentalAssetOfferingId: z.string().optional(),
    rentalAssetTypeId: z.string(),
    delta: z
        .number()
        .int()
        .refine((value) => value !== 0, 'delta must be non-zero'),
})

export type ApplyStockEquipmentAssignmentDeltaInput = z.input<
    typeof applyStockEquipmentAssignmentDeltaInputSchema
>

export const applyStockEquipmentReassignmentInputSchema = z.object({
    rentalAssetTypeId: z.string(),
    quantity: z.number().int().min(1),
})

export type ApplyStockEquipmentReassignmentInput = z.input<
    typeof applyStockEquipmentReassignmentInputSchema
>

export function mapStockEquipmentAssignmentSummary(
    raw: StockEquipmentAssignmentSummaryRaw,
): StockEquipmentAssignmentSummary {
    return {
        ...mapStockSource(raw),
        rentalAssetTypeId: raw.rental_asset_type_id,
        rentalAssetTypeName: raw.rental_asset_type_name,
        assignedQuantity: raw.assigned_quantity,
        assignableQuantity: raw.assignable_quantity ?? null,
        appliedDelta: raw.applied_delta ?? undefined,
        heldElsewhere: raw.held_elsewhere.map((held) => ({
            jobId: held.job_id,
            jobName: held.job_name,
            quantity: held.quantity,
        })),
    }
}

export function totalHeldElsewhereQuantity(
    heldElsewhere: StockHeldElsewhere[] | undefined,
): number {
    if (!heldElsewhere || heldElsewhere.length === 0) {
        return 0
    }
    return heldElsewhere.reduce((sum, held) => sum + held.quantity, 0)
}

export const jobStockEquipmentGroupRawSchema = z.object({
    ...stockSourceRawFields,
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    assigned_quantity: z.number().int().nonnegative(),
})
export const jobStockEquipmentGroupSchema = jobStockEquipmentGroupRawSchema.transform((raw) => ({
    ...mapStockSource(raw),
    rentalAssetTypeId: raw.rental_asset_type_id,
    rentalAssetTypeName: raw.rental_asset_type_name,
    assignedQuantity: raw.assigned_quantity,
}))
export type JobStockEquipmentGroup = z.infer<typeof jobStockEquipmentGroupSchema>
